import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, Loader2 } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`p-1 transition-colors ${n <= value ? 'text-amber-400' : 'text-white/40 hover:text-amber-300'}`}
        aria-label={`${n} stars`}
      >
        <Star size={22} strokeWidth={1.4} fill={n <= value ? 'currentColor' : 'none'} />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ r, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.6, delay: idx * 0.1 }}
    className="bg-white/15 backdrop-blur-md rounded-3xl border border-white/25 p-6 md:p-7 shadow-xl h-full flex flex-col"
  >
    <div className="flex items-center gap-1 mb-3 text-amber-400">
      {Array.from({ length: r.stars }).map((_, i) => (
        <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
      ))}
    </div>
    <p className="text-white/95 italic font-light text-base md:text-lg leading-relaxed flex-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      &ldquo;{r.text}&rdquo;
    </p>
    <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-between">
      <span className="text-white tracking-[0.15em] text-sm font-light" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
        {r.name}
      </span>
      {r.treatment && (
        <span className="text-white/70 text-xs" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {r.treatment}
        </span>
      )}
    </div>
  </motion.div>
);

const Reviews = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: '', stars: 5, text: '', treatment: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    publicApi.get('/reviews').then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast({ title: 'Please add your name and review' });
      return;
    }
    setSending(true);
    try {
      await publicApi.post('/reviews', form);
      toast({ title: 'Thank you \u2728', description: 'Dawn will read it and publish it soon.' });
      setForm({ name: '', stars: 5, text: '', treatment: '' });
    } catch {
      toast({ title: 'Could not send review', description: 'Please try again shortly.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-12">
          <p className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            REVIEWS
          </p>
          <h2 className="text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            Words from happy clients
          </h2>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        {reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {reviews.slice(0, 6).map((r, i) => (
              <ReviewCard key={r.id} r={r} idx={i} />
            ))}
          </div>
        )}

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8 }}
          onSubmit={submit}
          className="max-w-2xl mx-auto bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-8 md:p-10 shadow-2xl"
        >
          <p className="text-white/80 tracking-[0.25em] text-xs mb-6 text-center" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            LEAVE A REVIEW AFTER YOUR TREATMENT
          </p>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>YOUR NAME</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="First name & surname initial" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
              <div>
                <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>TREATMENT (OPTIONAL)</label>
                <input value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })}
                  className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none"
                  placeholder="e.g. Deep tissue" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>YOUR RATING</label>
              <StarPicker value={form.stars} onChange={(v) => setForm({ ...form, stars: v })} />
            </div>

            <div>
              <label className="block text-white/80 text-xs tracking-[0.25em] mb-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>YOUR REVIEW</label>
              <textarea rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-2 outline-none resize-none"
                placeholder="Share how your treatment felt..."
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }} />
            </div>
          </div>

          <button type="submit" disabled={sending}
            className="mt-6 inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] font-light bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500 disabled:opacity-60"
            style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            {sending ? (<>SENDING <Loader2 size={14} className="animate-spin" /></>) : (<>SUBMIT REVIEW <Send size={14} /></>)}
          </button>

          <p className="mt-4 text-xs text-white/60" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Reviews appear on the site once Dawn has read them. Thank you for sharing your experience!
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Reviews;
